
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, UserCog, CheckCircle, Info, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfileCheck } from "@/contexts/ProfileCheckContext";

interface CustomerFormProps {
  note: string;
  onNoteChange: (value: string) => void;
  isAuthenticated: boolean;
  userProfile?: any;
}

export const CustomerForm = ({
  note,
  onNoteChange,
  isAuthenticated,
  userProfile,
}: CustomerFormProps) => {
  const { redirectToProfile } = useProfileCheck();
  
  // Check if profile has required contact information
  const hasRequiredInfo = userProfile && 
    userProfile.phone && 
    userProfile.address;

  // Simplified message since we no longer track specific missing fields
  const showProfileIncomplete = userProfile && (
    !userProfile.display_name || 
    !userProfile.phone || 
    !userProfile.address
  );
  
  // Create a handler function that calls redirectToProfile with 'booking' reason
  const handleRedirectToProfile = () => {
    redirectToProfile('booking');
  };
  
  return (
    <>
      {!isAuthenticated ? (
        <div className="mb-4 p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
          <p className="text-sm">
            Please sign in to book appointments. Your profile information will be used for the booking.
          </p>
        </div>
      ) : !hasRequiredInfo ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4 mr-2" />
          <AlertDescription className="flex flex-col gap-2">
            <div className="space-y-1">
              <p className="font-medium">Your profile is missing required contact information.</p>
              <p className="text-sm">Please complete your display name, phone number, and address.</p>
              <p className="text-sm">We need this information to process your booking.</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="self-start mt-2"
              onClick={handleRedirectToProfile}
            >
              <UserCog className="mr-2 h-4 w-4" />
              Complete Your Profile
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="info" className="mb-4">
          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
          <AlertDescription>
            <div className="space-y-1">
              <p className="font-medium text-green-700">Your profile information is complete!</p>
              <p className="text-sm">
                We'll use your contact details:
                <span className="block mt-1">
                  <strong>Phone:</strong> {userProfile.phone}
                </span>
                <span className="block">
                  <strong>Address:</strong> {userProfile.address}
                </span>
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-medium">
          <FileText className="w-4 h-4 mr-1 text-gray-500" />
          Additional Notes (optional)
        </label>
        <Textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Any special requests or information you'd like to add..."
          rows={4}
          className={!isAuthenticated ? "opacity-70" : ""}
        />
      </div>
    </>
  );
};
